import * as React from 'react';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CallMergeIcon from '@mui/icons-material/CallMerge';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import HistoryIcon from '@mui/icons-material/History';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import DownloadIcon from '@mui/icons-material/Download';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
    getDuplicateReportSheet,
    getHelsinkiDate,
    getSchoolReportFilename,
} from './mergeReport';
import { isLearnerSchoolLicenseValid } from './licenseValidity';
import {
    getOverlappingSchoolLicenseRows,
    getSchoolLicenseSubscriptionGuid,
} from './licenseOverlap';
import { SchoolMergeContext } from './mergeNavigation';
import BulkMergeDialog from './BulkMergeDialog';
import { MergeProgress, runMergeBatch } from './mergeBatch';
import { alpha } from '@mui/material/styles';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Chip,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    MenuItem,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Tooltip,
} from '@mui/material';
import {
    ListPaginationContext,
    Pagination,
    useDataProvider,
    useNotify,
    useStore,
    useTranslate,
} from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';
import { StatsTooltip } from '../../components/statistics/common';

const APPLICATIONS = {
    pupil: '5b846602-4428-4c2d-b12a-d2b56dd722b7',
    student: 'a7faf0f2-3cfc-4f62-9a77-aa75134ac5a4',
    teacher: '0e274549-7a89-4313-8720-0f4588a51f98',
} as const;

type UserResource = keyof typeof APPLICATIONS;
type SortField = 'last_login' | 'licenses' | 'registered';

interface AccountLicense {
    guid?: string;
    status?: number;
    type?: number;
    valid_from?: string | null;
    valid_until?: string | null;
    subscription_valid_from?: string | null;
    subscription_valid_until?: string | null;
    materialstorage_guid?: string;
    product_guid?: string;
    name?: string;
    subscriber_guid?: string;
    subscriber_kind?: 'school' | 'user' | 'unknown';
    subscriber_name?: string;
    is_school_license?: boolean;
}

type LicenseOwnerCategory = 'school' | 'other_school' | 'own' | 'unknown';
type LicenseOwnerColor = 'success' | 'info' | 'secondary' | 'default';

interface Account {
    id: string;
    guid: string;
    username?: string;
    status: number;
    public?: {
        firstname?: string;
        lastname?: string;
        email?: string;
        code?: string;
    };
    last_login?: string;
    registered?: string;
    schools?: Array<{ guid: string; name: string }>;
    application?: Array<{ guid: string; name?: string }>;
    licenses?: AccountLicense[];
    classes?: Array<{ name?: string; archived?: boolean }>;
    private?: { services?: Record<string, unknown> };
    services?: Record<string, unknown> | string[];
}

interface DuplicateGroup {
    key: string;
    name: string;
    accounts: Account[];
    schoolName: string;
    latestLogin: number;
    latestRegistered: number;
    validLicenseCount: number;
    overlappingLicenseCount: number;
    blockedReason?: string;
}

interface GroupSelection {
    primaryGuid?: string;
    identifierGuid?: string;
}

interface LastExportSelection {
    version: 1;
    groups: Array<GroupSelection & { accountGuids: string[] }>;
}

interface PairPreview {
    groupKey: string;
    groupName: string;
    toGuid: string;
    fromGuid: string;
    warnings: string[];
    error?: string;
}

interface GroupResult {
    groupKey: string;
    groupName: string;
    status: 'success' | 'partial' | 'error' | 'cancelled';
    message?: string;
    historyFetchFailed?: boolean;
    merges?: Array<{
        guid: string;
        from?: { guid?: string; email?: string; username?: string };
        cancellable: boolean;
        materials_merged: boolean;
    }>;
}

const AccountCreatedDate = ({
    registered,
    resource,
}: {
    registered?: string;
    resource: UserResource;
}) => {
    const translate = useTranslate();
    const date = formatAccountCreatedDate(registered) ?? '—';
    const warning = getStudyYearWarning(registered, resource);
    if (!warning) return <>{date}</>;
    const warningText = translate('merge.bulk.study_year_warning', warning);
    return (
        <StatsTooltip
            describeChild
            title={
                <Box sx={{ p: 1.5, maxWidth: 320 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        {translate('merge.bulk.account_created')}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{ whiteSpace: 'pre-line' }}
                    >
                        {warningText}
                    </Typography>
                </Box>
            }
        >
            <Box
                component="span"
                tabIndex={0}
                aria-label={`${date}. ${warningText}`}
                sx={theme => ({
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 1,
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                    color: theme.palette.warning.dark,
                    backgroundColor: alpha(theme.palette.warning.main, 0.15),
                })}
            >
                <WarningAmberIcon fontSize="small" />
                {date}
            </Box>
        </StatsTooltip>
    );
};

const getLogin = (account: Account, resource: UserResource) => {
    if (resource === 'pupil') return account.public?.code || '';
    if (resource === 'student') return account.public?.email || '';
    return account.username || account.public?.email || '';
};

const getEmailDomain = (identifier: string) => {
    const match = identifier.trim().match(/^[^@\s]+@([^@\s]+)$/);
    return match ? `@${match[1].toLowerCase()}` : '';
};

const getFullName = (account: Account) =>
    [account.public?.firstname, account.public?.lastname]
        .filter(Boolean)
        .join(' ')
        .trim();

const getServices = (account: Account) => {
    if (Array.isArray(account.services)) return account.services;
    return Object.keys(account.services || account.private?.services || {});
};

const getBlockedReason = (accounts: Account[]) => {
    if (
        accounts.filter(account => getServices(account).includes('mpass'))
            .length > 1
    )
        return 'merge.bulk.multiple_mpass';
    if (accounts.some(account => (account.application?.length || 0) > 1))
        return 'merge.bulk.multiple_applications';
    return undefined;
};

const getLoginTime = (account: Account) =>
    new Date(account.last_login || 0).getTime() || 0;

const getCurrentSeasonStart = (now = new Date()) =>
    new Date(now.getFullYear(), 7, 1).getTime();

const hasAccountLoggedInThisSeason = (
    group: DuplicateGroup,
    now = new Date()
) => {
    const seasonStart = getCurrentSeasonStart(now);
    return group.accounts.some(
        account => getLoginTime(account) >= seasonStart
    );
};

const getRegisteredTime = (account: Account) =>
    new Date(account.registered || 0).getTime() || 0;

const formatAccountCreatedDate = (registered?: string) => {
    if (!registered) return null;
    const created = new Date(registered);
    if (Number.isNaN(created.getTime())) return null;
    return created.toLocaleDateString('fi-FI');
};

const getStudyYearWarning = (
    registered: string | undefined,
    resource: UserResource,
    now = new Date()
) => {
    if (!registered || resource === 'teacher') return null;
    const created = new Date(registered);
    if (Number.isNaN(created.getTime()) || created > now) return null;
    const maxStudyYears = resource === 'student' ? 3 : 9;
    const firstStudyYear =
        created.getFullYear() - (created.getMonth() < 7 ? 1 : 0);
    const currentStudyYear =
        now.getFullYear() - (now.getMonth() < 7 ? 1 : 0);
    const studyYear = currentStudyYear - firstStudyYear + 1;
    return studyYear > maxStudyYears ? { studyYear, maxStudyYears } : null;
};

const isValidLicense = (
    license: AccountLicense,
    now = Date.now(),
    isLearner = false
) =>
    (license.status === undefined || license.status === 0) &&
    (isLearner && license.type === 0
        ? isLearnerSchoolLicenseValid(license, now)
        : (!license.valid_from ||
              new Date(license.valid_from).getTime() <= now) &&
          (!license.valid_until ||
              new Date(license.valid_until).getTime() >= now));

const getValidLicenses = (account: Account, isLearner = false) => {
    const now = Date.now();
    return (account.licenses || []).filter(license =>
        isValidLicense(license, now, isLearner)
    );
};

const isActiveLicense = (license: AccountLicense) => license.status === 0;

const getActiveLicenses = (account: Account) =>
    (account.licenses || []).filter(isActiveLicense);

const getDisplayLicenses = (licenses: AccountLicense[], isLearner = false) => {
    const now = Date.now();
    const grouped = new Map<
        string,
        {
            license: AccountLicense;
            licenses: AccountLicense[];
            versionCount: number;
            validVersionCount: number;
        }
    >();
    licenses
        .filter(license => Boolean(license.name))
        .forEach(license => {
            const key = license.product_guid
                ? `product:${license.product_guid}:${license.subscriber_guid || ''}`
                : `name:${license.name}:${license.subscriber_guid || ''}`;
            const existing = grouped.get(key);
            if (existing) {
                existing.licenses.push(license);
                existing.versionCount += 1;
                if (isValidLicense(license, now, isLearner))
                    existing.validVersionCount += 1;
            } else {
                grouped.set(key, {
                    license,
                    licenses: [license],
                    versionCount: 1,
                    validVersionCount: isValidLicense(license, now, isLearner)
                        ? 1
                        : 0,
                });
            }
        });
    return Array.from(grouped.values());
};

const getValidLicenseCount = (account: Account, isLearner = false) =>
    getValidLicenses(account, isLearner).length;

const getSchoolLicenseCount = (account: Account) =>
    getActiveLicenses(account).filter(license => license.is_school_license)
        .length;

const getValidSchoolLicenseCount = (account: Account, isLearner = false) =>
    getValidLicenses(account, isLearner).filter(
        license => license.is_school_license
    ).length;

const getLicenseOwnerCategory = (
    license: AccountLicense
): LicenseOwnerCategory => {
    if (license.is_school_license) return 'school';
    if (license.subscriber_kind === 'school') return 'other_school';
    if (license.subscriber_kind === 'user') return 'own';
    return 'unknown';
};

const getLicenseOwnerColor = (
    category: LicenseOwnerCategory
): LicenseOwnerColor => {
    switch (category) {
        case 'school':
            return 'success';
        case 'other_school':
            return 'info';
        case 'own':
            return 'secondary';
        default:
            return 'default';
    }
};

const getValidLicenseOwnerCounts = (account: Account, isLearner = false) =>
    getValidLicenses(account, isLearner).reduce(
        (counts, license) => {
            counts[getLicenseOwnerCategory(license)] += 1;
            return counts;
        },
        { school: 0, other_school: 0, own: 0, unknown: 0 }
    );

const getActiveLicenseOwnerCounts = (account: Account) =>
    getActiveLicenses(account).reduce(
        (counts, license) => {
            counts[getLicenseOwnerCategory(license)] += 1;
            return counts;
        },
        { school: 0, other_school: 0, own: 0, unknown: 0 }
    );

const getLicenseOwnerLabel = (
    license: AccountLicense,
    account: Account,
    translate: (key: string, options?: Record<string, unknown>) => string
) => {
    if (license.is_school_license)
        return translate('merge.bulk.license_owner_school');
    if (license.subscriber_kind === 'user')
        return translate(
            license.subscriber_guid === account.guid
                ? 'merge.bulk.license_owner_account'
                : 'merge.bulk.license_owner_other_account',
            { account: license.subscriber_name || '—' }
        );
    if (license.subscriber_kind === 'school')
        return translate('merge.bulk.license_owner_other_school', {
            school: license.subscriber_name || '—',
        });
    return translate('merge.bulk.license_owner_unknown');
};

const getLicenseTooltipDetails = ({
    ownerCategory,
    ownerLabel,
    isOverlapping,
    validVersionCount,
    versionCount,
    extendedLabel,
    translate,
}: {
    ownerCategory: LicenseOwnerCategory;
    ownerLabel: string;
    isOverlapping: boolean;
    validVersionCount: number;
    versionCount: number;
    extendedLabel: string;
    translate: (
        key: string,
        options?: Record<string, unknown>
    ) => string;
}) =>
    ({
        title: translate(`merge.bulk.license_category_${ownerCategory}`),
        details: [
        ownerCategory === 'school'
            ? translate(`merge.bulk.license_category_${ownerCategory}_help`)
            : '',
        ownerCategory === 'school' && isOverlapping
            ? translate('merge.bulk.overlapping_license')
            : '',
        ownerCategory !== 'school'
            ? translate(`merge.bulk.license_category_${ownerCategory}_help`)
            : '',
        ownerCategory === 'school' && isOverlapping
            ? translate('merge.bulk.license_category_school_release_help')
            : '',
        translate(
            validVersionCount > 0
                ? 'merge.bulk.license_currently_valid'
                : 'merge.bulk.license_not_currently_valid'
        ),
        ownerCategory === 'school' &&
        validVersionCount > 0 &&
        versionCount > 1
            ? translate('merge.bulk.license_validity_summary', {
                  valid: validVersionCount,
                  active: versionCount,
              })
            : '',
        extendedLabel,
        ].filter(Boolean),
    });

const getOverlappingLicenseSummary = (
    accounts: Account[],
    isLearner = false,
    now = Date.now()
) =>
    getOverlappingSchoolLicenseRows(
        accounts.map(account => account.licenses || []),
        license =>
            license.is_school_license === true &&
            isValidLicense(license, now, isLearner)
    );

const isOverlappingLicense = (
    license: AccountLicense,
    overlappingSubscriptionGuids: Set<string>,
    isLearner = false,
    now = Date.now()
) => {
    const guid = getSchoolLicenseSubscriptionGuid(license);
    return Boolean(
        guid &&
            overlappingSubscriptionGuids.has(guid) &&
            license.is_school_license === true &&
            isValidLicense(license, now, isLearner)
    );
};

const getOverlappingLicenseCount = (accounts: Account[], isLearner = false) =>
    getOverlappingLicenseSummary(accounts, isLearner).extraRows;

const formatDate = (value?: string) =>
    value
        ? new Date(value).toLocaleString('fi-FI', {
              dateStyle: 'short',
              timeStyle: 'short',
          })
        : '—';

const normalizeName = (value?: string) =>
    (value || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('fi-FI');

const getFirstGivenName = (value?: string) =>
    normalizeName(value).split(' ')[0] || '';

const getVisibleGroups = (account: Account) =>
    (account.classes || []).filter(item => item.archived !== true);

const getVisibleGroupCount = (account: Account) =>
    getVisibleGroups(account).length;

const getOverlappingGroupNames = (accounts: Account[]) => {
    const groupAccountCounts = new Map<string, number>();
    accounts.forEach(account => {
        const groupNames = new Set(
            getVisibleGroups(account)
                .map(item => normalizeName(item.name))
                .filter(Boolean)
        );
        groupNames.forEach(name =>
            groupAccountCounts.set(
                name,
                (groupAccountCounts.get(name) || 0) + 1
            )
        );
    });
    return new Set(
        Array.from(groupAccountCounts.entries())
            .filter(([, accountCount]) => accountCount > 1)
            .map(([name]) => name)
    );
};

const getGroupNames = (group: DuplicateGroup, primaryGuid?: string) => {
    const names = new Map<string, { name: string; primary: boolean }>();
    const accounts = [
        ...group.accounts.filter(account => account.guid === primaryGuid),
        ...group.accounts.filter(account => account.guid !== primaryGuid),
    ];
    accounts.forEach(account => {
        const name = getFullName(account).replace(/\s+/g, ' ');
        const key = normalizeName(name);
        if (key && !names.has(key))
            names.set(key, { name, primary: account.guid === primaryGuid });
    });
    return names.size
        ? Array.from(names.values())
        : [{ name: group.name, primary: false }];
};

const matchesGroupSearch = (group: DuplicateGroup, search: string) => {
    const terms = normalizeName(search).split(' ').filter(Boolean);
    return group.accounts.some(account => {
        const values = [
            group.name,
            account.public?.firstname,
            account.public?.lastname,
            account.public?.email,
            account.public?.code,
            account.username,
            account.guid,
        ].map(normalizeName);
        return terms.every(term => values.some(value => value.includes(term)));
    });
};

const matchesGroupFilters = (
    group: DuplicateGroup,
    search: string,
    loggedInThisSeason: boolean,
    overlappingLicensesOnly: boolean
) =>
    matchesGroupSearch(group, search) &&
    (!loggedInThisSeason || hasAccountLoggedInThisSeason(group)) &&
    (!overlappingLicensesOnly || group.overlappingLicenseCount > 0);

const isGroupExportLocked = (group: DuplicateGroup) =>
    getBlockedReason(group.accounts) === 'merge.bulk.multiple_mpass';

const getExportGroups = (
    sortedGroups: DuplicateGroup[],
    excludedKeys: string[]
) =>
    sortedGroups.filter(
        group => !isGroupExportLocked(group) && !excludedKeys.includes(group.key)
    );

const createLastExportSelection = (
    groups: DuplicateGroup[],
    selections: Record<string, GroupSelection>,
    results: Record<string, GroupResult>
): LastExportSelection => ({
    version: 1,
    groups: groups
        .filter(
            group =>
                !group.blockedReason &&
                (!results[group.key] || results[group.key].status === 'cancelled')
        )
        .map(group => ({
            accountGuids: group.accounts.map(account => account.guid),
            primaryGuid: selections[group.key]?.primaryGuid,
            identifierGuid: selections[group.key]?.identifierGuid,
        })),
});

const isLastExportSelection = (value: unknown): value is LastExportSelection =>
    typeof value === 'object' &&
    value !== null &&
    'version' in value &&
    value.version === 1 &&
    'groups' in value &&
    Array.isArray(value.groups) &&
    value.groups.every(
        group =>
            typeof group === 'object' &&
            group !== null &&
            Array.isArray(group.accountGuids) &&
            group.accountGuids.length >= 2 &&
            group.accountGuids.every(
                (guid: unknown) => typeof guid === 'string' && guid.length > 0
            ) &&
            new Set(group.accountGuids).size === group.accountGuids.length &&
            [group.primaryGuid, group.identifierGuid].every(
                guid => guid === undefined || group.accountGuids.includes(guid)
            )
    );

const getLastExportSelections = (
    snapshot: LastExportSelection,
    groups: DuplicateGroup[],
    results: Record<string, GroupResult>
) => {
    const restored: Record<string, GroupSelection> = {};
    for (const saved of snapshot.groups) {
        const group = groups.find(
            candidate =>
                candidate.accounts.length === saved.accountGuids.length &&
                candidate.accounts.every(account =>
                    saved.accountGuids.includes(account.guid)
                )
        );
        if (
            !group ||
            group.blockedReason ||
            getBlockedReason(group.accounts) ||
            (results[group.key] && results[group.key].status !== 'cancelled')
        )
            continue;
        restored[group.key] = {
            primaryGuid: saved.primaryGuid,
            identifierGuid: saved.identifierGuid,
        };
    }
    return restored;
};

const BulkMergePage = ({
    resource: resourceParam,
    school: schoolGuid,
    mode,
    onBusyChange,
}: SchoolMergeContext & { mode: 'export' | 'merge' }) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const translate = useTranslate();

    const resource = Object.keys(APPLICATIONS).includes(resourceParam)
        ? (resourceParam as UserResource)
        : null;
    const isLearner = resource === 'student' || resource === 'pupil';
    const [lastExportSelection, setLastExportSelection] = useStore<unknown>(
        `merge.lastExport.${schoolGuid}.${resourceParam}`,
        null
    );
    const validLastExport = isLastExportSelection(lastExportSelection);
    const { canAccess: canWrite, isPending: writePending } = useCanAccess({
        action: 'write',
        resource: resource || 'user',
    });
    const { canAccess: canRead, isPending: readPending } = useCanAccess({
        action: 'read',
        resource: resource || 'user',
    });
    const canAccess = mode === 'export' ? canRead : canWrite;
    const permissionLoading = mode === 'export' ? readPending : writePending;
    const { canAccess: canCancelMerge } = useCanAccess({
        action: 'cancel_merge',
        resource: resource || 'user',
    });

    const [groups, setGroups] = React.useState<DuplicateGroup[]>([]);
    const [selections, setSelections] = React.useState<
        Record<string, GroupSelection>
    >({});
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    const [sortField, setSortField] = React.useState<SortField>('last_login');
    const [search, setSearch] = React.useState('');
    const [loggedInThisSeason, setLoggedInThisSeason] =
        React.useState(false);
    const [overlappingLicensesOnly, setOverlappingLicensesOnly] =
        React.useState(false);
    const [exporting, setExporting] = React.useState(false);
    const [identifierDomain, setIdentifierDomain] = React.useState('');
    const [page, setPage] = React.useState(0);
    const [selectedKeys, setSelectedKeys] = React.useState<string[]>([]);
    const [excludedExportKeys, setExcludedExportKeys] =
        React.useState<string[]>([]);
    const [previews, setPreviews] = React.useState<PairPreview[] | null>(null);
    const [previewLoading, setPreviewLoading] = React.useState(false);
    const [mergeLoading, setMergeLoading] = React.useState(false);
    const [mergeProgress, setMergeProgress] =
        React.useState<MergeProgress | null>(null);
    const [stopRequested, setStopRequested] = React.useState(false);
    const mergeRunningRef = React.useRef(false);
    const mergeStopRef = React.useRef(false);
    const [results, setResults] = React.useState<Record<string, GroupResult>>(
        {}
    );
    const [cancelTarget, setCancelTarget] = React.useState<{
        groupKey: string;
        merge: NonNullable<GroupResult['merges']>[number];
    } | null>(null);
    const [cancelLoading, setCancelLoading] = React.useState(false);
    const [expandedAccounts, setExpandedAccounts] = React.useState<
        Record<string, boolean>
    >({});
    const [pageSize, setPageSize] = React.useState(25);
    React.useEffect(() => {
        onBusyChange?.(
            exporting || previewLoading || mergeLoading || cancelLoading
        );
        return () => onBusyChange?.(false);
    }, [exporting, previewLoading, mergeLoading, cancelLoading, onBusyChange]);

    const loadGroups = React.useCallback(
        async (clearResultKey?: string, preserveResults = false) => {
            if (!resource || !schoolGuid) {
                setError(translate('merge.bulk.invalid_context'));
                setLoading(false);
                return;
            }

            setLoading(true);
            setError('');
            try {
                const pageSizeFromApi = 1000;
                const accounts: Account[] = [];
                let currentPage = 1;
                let total = Number.POSITIVE_INFINITY;

                while (accounts.length < total) {
                    const response = await dataProvider.getList(resource, {
                        filter: {
                            status: 10,
                            parent_group: schoolGuid,
                            application: APPLICATIONS[resource],
                            licenses: true,
                            bulk_merge: true,
                            need_count: true,
                        },
                        pagination: {
                            page: currentPage,
                            perPage: pageSizeFromApi,
                        },
                        sort: { field: 'registered', order: 'DESC' },
                    });
                    accounts.push(...(response.data as Account[]));
                    total = response.total ?? accounts.length;
                    if (response.data.length < pageSizeFromApi) break;
                    currentPage += 1;
                }

                const grouped = new Map<string, DuplicateGroup>();
                accounts.forEach(account => {
                    const firstname = account.public?.firstname || '';
                    const duplicateFirstname =
                        resource === 'teacher'
                            ? normalizeName(firstname)
                            : normalizeName(firstname.split(' ')[0]);
                    const lastname = normalizeName(account.public?.lastname);
                    if (!duplicateFirstname || !lastname) return;

                    const key = `${schoolGuid}:${duplicateFirstname}:${lastname}`;
                    const schoolName =
                        account.schools?.find(
                            school => school.guid === schoolGuid
                        )?.name || schoolGuid;
                    const group = grouped.get(key) || {
                        key,
                        name: getFullName(account),
                        accounts: [],
                        schoolName,
                        latestLogin: 0,
                        latestRegistered: 0,
                        validLicenseCount: 0,
                        overlappingLicenseCount: 0,
                    };
                    if (
                        !group.accounts.some(item => item.guid === account.guid)
                    ) {
                        group.accounts.push(account);
                        group.latestLogin = Math.max(
                            group.latestLogin,
                            getLoginTime(account)
                        );
                        group.latestRegistered = Math.max(
                            group.latestRegistered,
                            getRegisteredTime(account)
                        );
                        group.validLicenseCount +=
                            getValidLicenseCount(account, isLearner);
                    }
                    grouped.set(key, group);
                });

                const duplicateGroups = Array.from(grouped.values())
                    .filter(group => group.accounts.length > 1)
                    .map(group => {
                        const blockedReason = getBlockedReason(group.accounts);
                        return {
                            ...group,
                            overlappingLicenseCount: getOverlappingLicenseCount(
                                group.accounts,
                                isLearner
                            ),
                            blockedReason: blockedReason
                                ? translate(blockedReason)
                                : undefined,
                            accounts: [...group.accounts].sort((a, b) =>
                                getFullName(a).localeCompare(
                                    getFullName(b),
                                    'fi-FI'
                                )
                            ),
                        };
                    });

                setGroups(duplicateGroups);
                setSelections(
                    Object.fromEntries(
                        duplicateGroups.map(group => {
                            const ranked = [...group.accounts].sort(
                                (a, b) =>
                                    getLoginTime(b) - getLoginTime(a) ||
                                    getValidLicenseCount(b, isLearner) -
                                    getValidLicenseCount(a, isLearner)
                            );
                            const first = ranked[0];
                            const second = ranked[1];
                            const tie =
                                getLoginTime(first) === getLoginTime(second) &&
                                getValidLicenseCount(first, isLearner) ===
                                getValidLicenseCount(second, isLearner);
                            const primaryGuid = tie ? undefined : first.guid;
                            return [
                                group.key,
                                {
                                    primaryGuid,
                                    identifierGuid: primaryGuid,
                                } satisfies GroupSelection,
                            ];
                        })
                    )
                );
                setSelectedKeys([]);
                setExcludedExportKeys([]);
                setIdentifierDomain('');
                if (clearResultKey) {
                    setResults(previous => {
                        const next = { ...previous };
                        delete next[clearResultKey];
                        return next;
                    });
                } else if (!preserveResults) {
                    setResults({});
                }
                setPreviews(null);
                setPage(0);
            } catch (fetchError) {
                console.error(fetchError);
                setError(translate('merge.bulk.load_error'));
            } finally {
                setLoading(false);
            }
        },
        [dataProvider, resource, isLearner, schoolGuid, translate]
    );

    React.useEffect(() => {
        if (canRead) loadGroups();
    }, [loadGroups, canRead]);

    const filteredGroups = React.useMemo(
        () =>
            groups.filter(group =>
                matchesGroupFilters(
                    group,
                    search,
                    loggedInThisSeason,
                    overlappingLicensesOnly
                )
            ),
        [groups, search, loggedInThisSeason, overlappingLicensesOnly]
    );
    const sortedGroups = React.useMemo(
        () =>
            [...filteredGroups].sort((a, b) => {
                const first =
                    sortField === 'last_login'
                        ? a.latestLogin
                        : sortField === 'licenses'
                          ? a.validLicenseCount
                          : a.latestRegistered;
                const second =
                    sortField === 'last_login'
                        ? b.latestLogin
                        : sortField === 'licenses'
                          ? b.validLicenseCount
                          : b.latestRegistered;
                return second - first || a.name.localeCompare(b.name, 'fi-FI');
            }),
        [filteredGroups, sortField]
    );
    const visibleGroups = sortedGroups.slice(
        page * pageSize,
        (page + 1) * pageSize
    );
    const exportGroups = getExportGroups(sortedGroups, excludedExportKeys);
    const exportDuplicates = async () => {
        if (
            !resource ||
            !canAccess ||
            exporting ||
            mergeLoading ||
            previewLoading
        )
            return;
        if (!exportGroups.length) {
            notify('merge.bulk.export_empty', { type: 'warning' });
            return;
        }
        setExporting(true);
        try {
            const exportedSelection = createLastExportSelection(
                exportGroups,
                selections,
                results
            );
            const reportGroups = exportGroups.map(group => {
                const selection = selections[group.key];
                const processed =
                    results[group.key] &&
                    results[group.key].status !== 'cancelled';
                const blocked = Boolean(group.blockedReason || processed);
                return {
                    note: group.blockedReason
                        ? `${translate('merge.bulk.report_review')}: ${group.blockedReason}`
                        : processed
                          ? translate('merge.bulk.report_processed')
                          : translate('merge.bulk.report_proposal'),
                    overlappingLicenseCount: group.overlappingLicenseCount,
                    accounts: group.accounts.map(account => ({
                        name: getFullName(account),
                        identifier: getLogin(account, resource),
                        mpass: getServices(account).includes('mpass'),
                        schoolLicenseCount: getSchoolLicenseCount(account),
                        validSchoolLicenseCount: getValidSchoolLicenseCount(
                            account,
                            isLearner
                        ),
                        primary:
                            !blocked && selection?.primaryGuid === account.guid,
                        retainIdentifier:
                            !blocked &&
                            selection?.identifierGuid === account.guid,
                    })),
                };
            });
            const { default: writeExcelFile } =
                await import('write-excel-file/browser');
            await writeExcelFile(
                getDuplicateReportSheet(
                    reportGroups,
                    groups[0]?.schoolName || '',
                    translate(`resources.${resource}.name`, { smart_count: 2 }),
                    search,
                    translate
                ),
                {
                    sheet: 'Tuplat',
                    columns: [
                        { width: 10 },
                        { width: 32 },
                        { width: 38 },
                        { width: 12 },
                        { width: 20 },
                        { width: 26 },
                        { width: 24 },
                        { width: 28 },
                        { width: 70 },
                    ],
                }
            ).toFile(
                getSchoolReportFilename(
                    'tuplat',
                    groups[0]?.schoolName || null,
                    resource,
                    getHelsinkiDate()
                )
            );
            try {
                setLastExportSelection(exportedSelection);
            } catch (saveError) {
                console.error(saveError);
                notify('merge.bulk.last_export_save_error', { type: 'error' });
            }
        } catch (err) {
            console.error(err);
            notify('merge.history.report_error', { type: 'error' });
        } finally {
            setExporting(false);
        }
    };

    const selectedGroups = groups.filter(
        group => !group.blockedReason && selectedKeys.includes(group.key)
    );
    const isGroupSelectable = (group: DuplicateGroup) =>
        !group.blockedReason &&
        (!results[group.key] || results[group.key].status === 'cancelled');

    const selectLastExport = () => {
        if (
            mode !== 'merge' ||
            !canWrite ||
            loading ||
            exporting ||
            previewLoading ||
            mergeLoading ||
            cancelLoading
        )
            return;
        if (!isLastExportSelection(lastExportSelection)) {
            notify('merge.bulk.last_export_unavailable', { type: 'warning' });
            return;
        }
        const restored = getLastExportSelections(
            lastExportSelection,
            groups,
            results
        );
        const keys = Object.keys(restored);
        setSelections(previous => ({ ...previous, ...restored }));
        setSelectedKeys(keys);
        setSearch('');
        setLoggedInThisSeason(false);
        setOverlappingLicensesOnly(false);
        setIdentifierDomain('');
        setPage(0);
        setPreviews(null);
        notify('merge.bulk.last_export_restored', {
            type: 'info',
            messageArgs: {
                count: keys.length,
                skipped: lastExportSelection.groups.length - keys.length,
            },
        });
    };

    const changeSortField = (field: SortField) => {
        setSortField(field);
        setPage(0);
        if (field !== 'licenses') return;

        setSelections(previous => {
            const next = { ...previous };
            groups.filter(isGroupSelectable).forEach(group => {
                const ranked = [...group.accounts].sort(
                    (a, b) =>
                        getValidLicenseCount(b, isLearner) -
                            getValidLicenseCount(a, isLearner) ||
                        getLoginTime(b) - getLoginTime(a)
                );
                const first = ranked[0];
                const second = ranked[1];
                const tie =
                    getValidLicenseCount(first, isLearner) ===
                        getValidLicenseCount(second, isLearner) &&
                    getLoginTime(first) === getLoginTime(second);
                next[group.key] = {
                    ...previous[group.key],
                    primaryGuid: tie ? undefined : first.guid,
                };
            });
            return next;
        });
        setPreviews(null);
    };

    const identifierDomains = React.useMemo(
        () =>
            resource && resource !== 'pupil'
                ? Array.from(
                      new Set(
                          groups.flatMap(group =>
                              group.accounts
                                  .map(account =>
                                      getEmailDomain(
                                          getLogin(account, resource)
                                      )
                                  )
                                  .filter(Boolean)
                          )
                      )
                  ).sort()
                : [],
        [groups, resource]
    );

    const applyIdentifierDomain = (domain: string) => {
        if (!resource || resource === 'pupil') return;
        setIdentifierDomain(domain);
        setSelections(previous => {
            const next = { ...previous };
            if (domain) {
                groups.filter(isGroupSelectable).forEach(group => {
                    const matches = group.accounts.filter(
                        account =>
                            getEmailDomain(getLogin(account, resource)) ===
                            domain
                    );
                    if (matches.length === 1) {
                        next[group.key] = {
                            ...previous[group.key],
                            identifierGuid: matches[0].guid,
                        };
                    }
                });
            }
            return next;
        });
        setPreviews(null);
    };

    const setGroupSelection = (group: DuplicateGroup, next: GroupSelection) => {
        if (!isGroupSelectable(group)) return;
        setSelections(previous => ({
            ...previous,
            [group.key]: next,
        }));
        setPreviews(null);
    };

    const toggleGroup = (group: DuplicateGroup, checked: boolean) => {
        if (checked && !isGroupSelectable(group)) return;
        setSelectedKeys(previous =>
            checked
                ? Array.from(new Set([...previous, group.key]))
                : previous.filter(key => key !== group.key)
        );
        setPreviews(null);
    };

    const selectExportPage = () => {
        const keys = visibleGroups
            .filter(group => !isGroupExportLocked(group))
            .map(group => group.key);
        const allIncluded = keys.every(key => !excludedExportKeys.includes(key));
        setExcludedExportKeys(previous =>
            allIncluded
                ? Array.from(new Set([...previous, ...keys]))
                : previous.filter(key => !keys.includes(key))
        );
    };

    const selectPage = (checked: boolean) => {
        const keys = visibleGroups
            .filter(isGroupSelectable)
            .map(group => group.key);
        setSelectedKeys(previous =>
            checked
                ? Array.from(new Set([...previous, ...keys]))
                : previous.filter(key => !keys.includes(key))
        );
        setPreviews(null);
    };

    const getPairWarnings = (master: Account, source: Account) => {
        const warnings: string[] = [];
        if (
            getFirstGivenName(master.public?.firstname) !==
            getFirstGivenName(source.public?.firstname)
        )
            warnings.push(translate('merge.conflict.firstname'));
        if (
            normalizeName(master.public?.lastname) !==
            normalizeName(source.public?.lastname)
        )
            warnings.push(translate('merge.conflict.lastname'));
        if (
            getServices(master).includes('mpass') &&
            getServices(source).includes('mpass')
        ) {
            warnings.push(translate('merge.conflict.mpass'));
        }
        if (getLoginTime(source) > getLoginTime(master)) {
            warnings.push(translate('merge.conflict.last_login'));
        }
        if (
            source.licenses?.some(license =>
                [
                    '9c6a67c9-0568-4abe-b650-f101b309aa70',
                    '25e6157a-72d1-42fd-a680-306d3fbc3438',
                ].includes(license.materialstorage_guid || '')
            )
        ) {
            warnings.push(translate('merge.conflict.restricted_material'));
        }
        return warnings;
    };

    const makeMergeRequest = (group: DuplicateGroup): PairPreview[] => {
        const selection = selections[group.key];
        if (!selection.primaryGuid || !selection.identifierGuid) return [];
        const master = group.accounts.find(
            account => account.guid === selection.primaryGuid
        );
        const identifier = group.accounts.find(
            account => account.guid === selection.identifierGuid
        );
        if (!master || !identifier) return [];

        const sources = group.accounts.filter(
            account => account.guid !== master.guid
        );
        sources.sort((a, b) => {
            if (a.guid === identifier.guid) return -1;
            if (b.guid === identifier.guid) return 1;
            return getLoginTime(b) - getLoginTime(a);
        });
        return sources.map(source => ({
            groupKey: group.key,
            groupName: group.name,
            toGuid: master.guid,
            fromGuid: source.guid,
            warnings: getPairWarnings(master, source),
        }));
    };

    const requestMergePair = async (
        pair: PairPreview,
        preview: boolean,
        retainIdentifier: boolean,
        batchId?: string
    ) => {
        const response = await fetch(
            `${process.env.REACT_APP_INFRA_API}/user/${pair.toGuid}/merge`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + localStorage.getItem('token'),
                },
                body: JSON.stringify([
                    {
                        to: pair.toGuid,
                        from: pair.fromGuid,
                        move: {
                            groups: true,
                            licenses: true,
                            extramaterials: true,
                            favorites: true,
                            email: true,
                            edustore: true,
                            mpass: true,
                            tasks: false,
                        },
                        must_be_same_school: true,
                        preview,
                        pass_refresh: true,
                        newemail: '',
                        retain_identifier_from: retainIdentifier,
                        batch_id: batchId,
                        action_after: 'RELEASE_EXTRA_LICENSES',
                    },
                ]),
            }
        );
        const body = await response.json();
        if (!response.ok) {
            const message =
                typeof body === 'string'
                    ? body
                    : body?.message || response.statusText;
            throw new Error(message);
        }
        return body;
    };

    const handlePreview = async () => {
        if (!canAccess || mode !== 'merge') return;
        setPreviewLoading(true);
        const allPairs = selectedGroups.flatMap(group =>
            makeMergeRequest(group)
        );
        const rows: PairPreview[] = [];
        try {
            for (const pair of allPairs) {
                const identifierGuid =
                    selections[pair.groupKey]?.identifierGuid;
                try {
                    await requestMergePair(
                        pair,
                        true,
                        pair.fromGuid === identifierGuid
                    );
                    rows.push(pair);
                } catch (previewError) {
                    rows.push({
                        ...pair,
                        error:
                            previewError instanceof Error
                                ? previewError.message
                                : translate('merge.bulk.preview_error'),
                    });
                }
            }
            setPreviews(rows);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleMerge = async () => {
        if (!canAccess || mode !== 'merge') return;
        if (
            mergeRunningRef.current ||
            !previews?.length ||
            previews.some(pair => pair.error)
        )
            return;

        const tasks = selectedGroups.map(group => ({
            group,
            pairs: makeMergeRequest(group),
        }));
        const currentPairs = tasks.flatMap(task => task.pairs);
        if (
            tasks.length === 0 ||
            tasks.some(task => task.pairs.length === 0) ||
            currentPairs.length !== previews.length ||
            currentPairs.some(
                (pair, index) =>
                    pair.groupKey !== previews[index].groupKey ||
                    pair.toGuid !== previews[index].toGuid ||
                    pair.fromGuid !== previews[index].fromGuid
            )
        ) {
            setPreviews(null);
            notify(translate('merge.bulk.preview_stale'), { type: 'warning' });
            return;
        }

        const startedKeys = new Set<string>();
        let stopped = false;
        let fatalError: string | undefined;
        mergeRunningRef.current = true;
        mergeStopRef.current = false;
        setStopRequested(false);
        setMergeProgress({
            total: tasks.length,
            processed: 0,
            succeeded: 0,
            partial: 0,
            failed: 0,
            historyErrors: 0,
            startedAt: performance.now(),
            stopped: false,
        });
        setMergeLoading(true);
        setPreviews(null);
        try {
            const batchId = crypto.randomUUID();
            ({ stopped } = await runMergeBatch(tasks, {
                isStopRequested: () => mergeStopRef.current,
                onGroupStarted: group => startedKeys.add(group.key),
                mergePair: async (pair, group) => {
                    await requestMergePair(
                        pair,
                        false,
                        pair.fromGuid ===
                            selections[group.key]?.identifierGuid,
                        batchId
                    );
                },
                onGroupResult: async ({
                    group,
                    completedPairs,
                    failure,
                    stopped: groupStopped,
                }) => {
                    const completedSources = new Set(
                        completedPairs.map(pair => pair.fromGuid)
                    );
                    const messages: string[] = [];
                    if (failure) {
                        messages.push(
                            failure.cause instanceof Error
                                ? failure.cause.message
                                : translate('merge.bulk.merge_error')
                        );
                    }
                    if (groupStopped)
                        messages.push(translate('merge.bulk.group_stopped'));

                    let historyFetchFailed = false;
                    let merges: GroupResult['merges'] = [];
                    try {
                        const response = await fetch(
                            `${process.env.REACT_APP_INFRA_API}/user/${selections[group.key]?.primaryGuid}/merge`,
                            {
                                headers: {
                                    Accept: 'application/json',
                                    Authorization:
                                        'Bearer ' +
                                        localStorage.getItem('token'),
                                },
                            }
                        );
                        if (!response.ok) throw new Error(response.statusText);
                        merges = (await response.json()).filter(merge =>
                            completedSources.has(merge.from?.guid || '')
                        );
                    } catch (fetchError) {
                        console.error(fetchError);
                        historyFetchFailed = true;
                        messages.push(translate('merge.bulk.results_error'));
                    }

                    const status: GroupResult['status'] = failure
                        ? completedSources.size > 0
                            ? 'partial'
                            : 'error'
                        : groupStopped
                          ? 'partial'
                          : 'success';
                    setResults(previous => ({
                        ...previous,
                        [group.key]: {
                            groupKey: group.key,
                            groupName: group.name,
                            status,
                            message: messages.join('; ') || undefined,
                            historyFetchFailed,
                            merges,
                        },
                    }));
                    const completedAt = performance.now();
                    setMergeProgress(previous =>
                        previous
                            ? {
                                  ...previous,
                                  processed: previous.processed + 1,
                                  succeeded:
                                      previous.succeeded +
                                      (status === 'success' ? 1 : 0),
                                  partial:
                                      previous.partial +
                                      (status === 'partial' ? 1 : 0),
                                  failed:
                                      previous.failed +
                                      (status === 'error' ? 1 : 0),
                                  historyErrors:
                                      previous.historyErrors +
                                      (historyFetchFailed ? 1 : 0),
                                  lastProcessedAt: completedAt,
                              }
                            : previous
                    );
                },
            }));
        } catch (error) {
            console.error(error);
            fatalError =
                error instanceof Error
                    ? error.message
                    : translate('merge.bulk.merge_error');
        } finally {
            setSelectedKeys(previous =>
                previous.filter(key => !startedKeys.has(key))
            );
            const finishedAt = performance.now();
            setMergeProgress(previous =>
                previous
                    ? {
                          ...previous,
                          finishedAt,
                          stopped,
                          fatalError,
                      }
                    : previous
            );
            mergeRunningRef.current = false;
            setMergeLoading(false);
        }
    };

    const handleStopMerge = () => {
        if (!mergeRunningRef.current || mergeStopRef.current) return;
        mergeStopRef.current = true;
        setStopRequested(true);
    };

    const handleCloseMergeDialog = () => {
        if (mergeRunningRef.current) return;
        setPreviews(null);
        setMergeProgress(null);
        setStopRequested(false);
    };

    const handleCancelMerge = async () => {
        if (!cancelTarget) return;
        setCancelLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_INFRA_API}/user/merge/${cancelTarget.merge.guid}/cancel`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization:
                            'Bearer ' + localStorage.getItem('token'),
                    },
                }
            );
            if (!response.ok) throw new Error(await response.text());
            setResults(previous => ({
                ...previous,
                [cancelTarget.groupKey]: {
                    ...previous[cancelTarget.groupKey],
                    status: 'cancelled',
                    message: translate('merge.profilepage.cancel_success'),
                    merges: previous[cancelTarget.groupKey]?.merges?.filter(
                        merge => merge.guid !== cancelTarget.merge.guid
                    ),
                },
            }));
            setCancelTarget(null);
            await loadGroups(undefined, true);
        } catch (cancelError) {
            console.error(cancelError);
            notify(translate('merge.profilepage.cancel_error'), {
                type: 'error',
            });
        } finally {
            setCancelLoading(false);
        }
    };

    if (!resource || !schoolGuid) {
        return (
            <Box p={3}>
                <Alert severity="error">
                    {translate('merge.bulk.invalid_context')}
                </Alert>
            </Box>
        );
    }

    if (!permissionLoading && !canAccess) {
        return (
            <Box p={3}>
                <Alert severity="warning">
                    {translate('merge.bulk.no_permission')}
                </Alert>
            </Box>
        );
    }

    return (
        <Box p={{ xs: 1, md: 3 }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ sm: 'center' }}
                justifyContent="space-between"
                mb={2}
            >
                <Box>
                    <Typography variant="h5">
                        {translate(
                            mode === 'export'
                                ? 'merge.bulk.report_title'
                                : 'merge.bulk.title'
                        )}
                    </Typography>
                    <Typography color="text.secondary">
                        {translate('merge.bulk.subtitle', {
                            school: groups[0]?.schoolName || schoolGuid,
                        })}
                    </Typography>
                </Box>
                {mode === 'export' && (
                    <Button
                        startIcon={
                            exporting ? (
                                <CircularProgress size={18} />
                            ) : (
                                <DownloadIcon />
                            )
                        }
                        disabled={
                            loading ||
                            exporting ||
                            mergeLoading ||
                            previewLoading ||
                            !exportGroups.length
                        }
                        onClick={exportDuplicates}
                    >
                        {translate('merge.workspace.export')}
                    </Button>
                )}
            </Stack>

            {error && <Alert severity="error">{error}</Alert>}
            {mode === 'merge' &&
                lastExportSelection !== null &&
                !validLastExport && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {translate('merge.bulk.last_export_invalid')}
                    </Alert>
                )}
            {loading || permissionLoading ? (
                <Stack alignItems="center" py={6}>
                    <CircularProgress />
                </Stack>
            ) : (
                <>
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            alignItems={{ md: 'center' }}
                            justifyContent="space-between"
                        >
                            <FormControl
                                size="small"
                                sx={{ minWidth: 240 }}
                                disabled={mergeLoading || previewLoading}
                            >
                                <FormLabel>
                                    {translate('merge.bulk.sort')}
                                </FormLabel>
                                <RadioGroup
                                    row
                                    value={sortField}
                                    onChange={event => {
                                        changeSortField(
                                            event.target.value as SortField
                                        );
                                    }}
                                >
                                    {(['last_login', 'licenses', 'registered'] as const).map(field => (
                                        <StatsTooltip
                                            key={field}
                                            describeChild
                                            title={
                                                <Box sx={{ p: 1.5, maxWidth: 320 }}>
                                                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                                        {translate(`merge.bulk.sort_${field}`)}
                                                    </Typography>
                                                    <Typography variant="body2">
                                                        {translate(`merge.bulk.sort_${field}_help`)}
                                                    </Typography>
                                                </Box>
                                            }
                                        >
                                            <FormControlLabel
                                                value={field}
                                                control={<Radio size="small" />}
                                                label={translate(`merge.bulk.sort_${field}`)}
                                            />
                                        </StatsTooltip>
                                    ))}
                                </RadioGroup>
                                {mode === 'merge' && (
                                    <Typography
                                        variant="caption"
                                        component="p"
                                        sx={{
                                            mt: 1,
                                            mb: 0,
                                            maxWidth: 600,
                                            color: 'warning.main',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        {translate(
                                            'merge.bulk.sort_merge_reminder'
                                        )}
                                    </Typography>
                                )}
                            </FormControl>
                        </Stack>
                        <TextField
                            size="small"
                            fullWidth
                            sx={{ mt: 2 }}
                            label={translate('merge.bulk.search')}
                            value={search}
                            disabled={previewLoading || mergeLoading}
                            onChange={event => {
                                setSearch(event.target.value);
                                setPage(0);
                                setSelectedKeys([]);
                                setPreviews(null);
                            }}
                        />
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={{ xs: 0, sm: 2 }}
                            sx={{ mt: 1 }}
                        >
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={loggedInThisSeason}
                                        disabled={previewLoading || mergeLoading}
                                        onChange={event => {
                                            setLoggedInThisSeason(
                                                event.target.checked
                                            );
                                            setPage(0);
                                            setSelectedKeys([]);
                                            setPreviews(null);
                                        }}
                                    />
                                }
                                label={translate(
                                    'merge.bulk.logged_in_this_season'
                                )}
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        size="small"
                                        checked={overlappingLicensesOnly}
                                        disabled={previewLoading || mergeLoading}
                                        onChange={event => {
                                            setOverlappingLicensesOnly(
                                                event.target.checked
                                            );
                                            setPage(0);
                                            setSelectedKeys([]);
                                            setPreviews(null);
                                        }}
                                    />
                                }
                                label={translate(
                                    'merge.bulk.overlapping_licenses_only'
                                )}
                            />
                        </Stack>
                        <Typography
                            variant="caption"
                            component="p"
                            color="text.secondary"
                            sx={{ mt: 0.5, mb: 0 }}
                        >
                            {translate('merge.bulk.filters_help')}
                        </Typography>
                        {resource !== 'pupil' && (
                            <TextField
                                select
                                size="small"
                                fullWidth
                                sx={{ mt: 2 }}
                                label={translate(
                                    'merge.bulk.identifier_domain'
                                )}
                                value={identifierDomain}
                                disabled={
                                    loading || previewLoading || mergeLoading
                                }
                                onChange={event =>
                                    applyIdentifierDomain(event.target.value)
                                }
                            >
                                <MenuItem value="">
                                    {translate(
                                        'merge.bulk.identifier_domain_none'
                                    )}
                                </MenuItem>
                                {identifierDomains.map(domain => (
                                    <MenuItem key={domain} value={domain}>
                                        {domain}
                                    </MenuItem>
                                ))}
                            </TextField>
                        )}
                    </Paper>

                    {groups.length === 0 && (
                        <Alert severity="info">
                            {translate('merge.bulk.no_groups')}
                        </Alert>
                    )}
                    {groups.length > 0 && filteredGroups.length === 0 && (
                        <Alert severity="info">
                            {translate('merge.bulk.no_search_results')}
                        </Alert>
                    )}

                    <Typography sx={{ mb: 1.5 }}>
                        {translate('merge.bulk.group_count', {
                            count: filteredGroups.length,
                        })}
                        <Typography
                            component="span"
                            variant="body2"
                            color="text.secondary"
                            display="block"
                            sx={{ whiteSpace: 'nowrap' }}
                        >
                            {translate(
                                mode === 'export'
                                    ? 'merge.bulk.export_group_count'
                                    : 'merge.bulk.merge_group_count',
                                {
                                    count:
                                        mode === 'export'
                                            ? exportGroups.length
                                            : selectedGroups.length,
                                    total: filteredGroups.length,
                                }
                            )}
                        </Typography>
                    </Typography>
                    <Stack
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{ mb: 1.5 }}
                    >
                        <Button
                            variant="outlined"
                            disabled={
                                loading ||
                                exporting ||
                                previewLoading ||
                                mergeLoading ||
                                cancelLoading
                            }
                            onClick={() =>
                                mode === 'export'
                                    ? selectExportPage()
                                    : selectPage(
                                          !visibleGroups
                                              .filter(isGroupSelectable)
                                              .every(group =>
                                                  selectedKeys.includes(group.key)
                                              )
                                      )
                            }
                        >
                            {translate('merge.bulk.select_page')}
                        </Button>
                        <Button
                            variant="outlined"
                            disabled={
                                loading ||
                                exporting ||
                                previewLoading ||
                                mergeLoading ||
                                cancelLoading ||
                                (mode === 'export'
                                    ? exportGroups.length === 0
                                    : selectedKeys.length === 0)
                            }
                            onClick={() => {
                                if (mode === 'export') {
                                    setExcludedExportKeys(
                                        groups.map(group => group.key)
                                    );
                                } else {
                                    setSelectedKeys([]);
                                    setPreviews(null);
                                }
                            }}
                        >
                            {translate('merge.bulk.clear_all_selection')}
                        </Button>
                        {mode === 'merge' && (
                            <StatsTooltip
                                describeChild
                                title={
                                    <Box sx={{ p: 1.5, maxWidth: 320 }}>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{ mb: 0.5 }}
                                        >
                                            {translate(
                                                'merge.bulk.select_last_export'
                                            )}
                                        </Typography>
                                        <Typography variant="body2">
                                            {translate(
                                                'merge.bulk.select_last_export_help'
                                            )}
                                        </Typography>
                                    </Box>
                                }
                            >
                                <Box component="span" sx={{ ml: 'auto' }}>
                                    <Button
                                        variant="outlined"
                                        startIcon={<HistoryIcon />}
                                        disabled={
                                            !validLastExport ||
                                            !canWrite ||
                                            loading ||
                                            exporting ||
                                            previewLoading ||
                                            mergeLoading ||
                                            cancelLoading
                                        }
                                        onClick={selectLastExport}
                                    >
                                        {translate(
                                            'merge.bulk.select_last_export'
                                        )}
                                    </Button>
                                </Box>
                            </StatsTooltip>
                        )}
                    </Stack>
                    <Stack spacing={1.5}>
                        {visibleGroups.map(group => {
                            const selection = selections[group.key] || {};
                            const result = results[group.key];
                            const selected =
                                mode === 'merge' &&
                                selectedKeys.includes(group.key);
                            const exportLocked = isGroupExportLocked(group);
                            const excludedFromExport =
                                mode === 'export' &&
                                (exportLocked ||
                                    excludedExportKeys.includes(group.key));
                            const overlappingSubscriptionGuids =
                                getOverlappingLicenseSummary(
                                    group.accounts,
                                    isLearner
                                ).subscriptionGuids;
                            const overlappingGroupNames =
                                getOverlappingGroupNames(group.accounts);
                            const groupTitle = (
                                <Box>
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        useFlexGap
                                        flexWrap="wrap"
                                    >
                                        {getGroupNames(
                                            group,
                                            selection.primaryGuid
                                        ).map(({ name, primary }) => (
                                            <Typography
                                                key={name}
                                                variant="h6"
                                                component="span"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                }}
                                            >
                                                <Tooltip
                                                    title={translate(
                                                        primary
                                                            ? 'merge.bulk.primary'
                                                            : 'merge.bulk.source_account'
                                                    )}
                                                >
                                                    {primary ? (
                                                        <PersonIcon
                                                            color="primary"
                                                            fontSize="small"
                                                            titleAccess={translate(
                                                                'merge.bulk.primary'
                                                            )}
                                                        />
                                                    ) : (
                                                        <PersonAddIcon
                                                            color="action"
                                                            fontSize="small"
                                                            titleAccess={translate(
                                                                'merge.bulk.source_account'
                                                            )}
                                                        />
                                                    )}
                                                </Tooltip>
                                                {name}
                                            </Typography>
                                        ))}
                                    </Stack>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {group.schoolName} ·{' '}
                                        {translate('merge.bulk.accounts_count', {
                                            count: group.accounts.length,
                                        })}
                                    </Typography>
                                </Box>
                            );
                            return (
                                <Paper
                                    key={group.key}
                                    sx={{
                                        p: 1.5,
                                        ...((mode === 'merge' && !selected) ||
                                            excludedFromExport
                                            ? {
                                                  border: '1px dashed',
                                                  borderColor: 'text.secondary',
                                              }
                                            : {}),
                                        ...(excludedFromExport && {
                                            backgroundColor: 'action.hover',
                                        }),
                                    }}
                                >
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        alignItems={{ sm: 'center' }}
                                        justifyContent="space-between"
                                        spacing={1}
                                    >
                                        {mode === 'export' ? (
                                            <Stack spacing={0.5}>
                                                {groupTitle}
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            size="small"
                                                            checked={
                                                                !excludedFromExport
                                                            }
                                                            disabled={
                                                                exportLocked ||
                                                                loading ||
                                                                exporting ||
                                                                previewLoading ||
                                                                mergeLoading
                                                            }
                                                            onChange={(_, checked) =>
                                                                setExcludedExportKeys(
                                                                    previous =>
                                                                        checked
                                                                            ? previous.filter(
                                                                                  key =>
                                                                                      key !== group.key
                                                                              )
                                                                            : [
                                                                                  ...previous,
                                                                                  group.key,
                                                                              ]
                                                                )
                                                            }
                                                            slotProps={{
                                                                input: {
                                                                    'aria-label': translate(
                                                                        'merge.bulk.export_include_group',
                                                                        { name: group.name }
                                                                    ),
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={translate(
                                                        excludedFromExport
                                                            ? 'merge.bulk.export_excluded'
                                                            : 'merge.bulk.export_included'
                                                    )}
                                                />
                                            </Stack>
                                        ) : (
                                            <Stack spacing={0.5}>
                                                {groupTitle}
                                                <FormControlLabel
                                                    control={
                                                        <Switch
                                                            size="small"
                                                            checked={selected}
                                                            disabled={
                                                                !isGroupSelectable(
                                                                    group
                                                                ) ||
                                                                loading ||
                                                                mergeLoading
                                                            }
                                                            onChange={(_, checked) =>
                                                                toggleGroup(
                                                                    group,
                                                                    checked
                                                                )
                                                            }
                                                            slotProps={{
                                                                input: {
                                                                    'aria-label': translate(
                                                                        'merge.bulk.merge_include_group',
                                                                        { name: group.name }
                                                                    ),
                                                                },
                                                            }}
                                                        />
                                                    }
                                                    label={translate(
                                                        'merge.bulk.merge_included'
                                                    )}
                                                />
                                            </Stack>
                                        )}
                                        <Stack
                                            direction="row"
                                            spacing={0.75}
                                            useFlexGap
                                            flexWrap="wrap"
                                        >
                                            {group.overlappingLicenseCount >
                                                0 && (
                                                <StatsTooltip
                                                    describeChild
                                                    title={translate(
                                                        'merge.bulk.overlapping_license_count_help'
                                                    )}
                                                >
                                                    <Chip
                                                        size="small"
                                                        color="warning"
                                                        tabIndex={0}
                                                        label={translate(
                                                            'merge.bulk.overlapping_licenses',
                                                            {
                                                                count: group.overlappingLicenseCount,
                                                            }
                                                        )}
                                                    />
                                                </StatsTooltip>
                                            )}
                                            {overlappingGroupNames.size >
                                                0 && (
                                                <Chip
                                                    size="small"
                                                    color="info"
                                                    label={translate(
                                                        'merge.bulk.overlapping_groups',
                                                        {
                                                            count: overlappingGroupNames.size,
                                                        }
                                                    )}
                                                />
                                            )}
                                        </Stack>
                                    </Stack>

                                    {group.blockedReason && (
                                        <Alert severity="error" sx={{ my: 1 }}>
                                            {group.blockedReason}
                                        </Alert>
                                    )}

                                    <Table
                                        size="small"
                                        sx={{
                                            mt: 0.5,
                                            '& .MuiTableCell-root': { py: 0.5 },
                                        }}
                                    >
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>
                                                    {translate(
                                                        'merge.bulk.primary'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {translate(
                                                        'merge.bulk.identifier'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {translate(
                                                        'merge.bulk.account'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {translate(
                                                        'merge.bulk.last_login'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {translate(
                                                        'merge.bulk.account_created'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {translate(
                                                        'merge.bulk.licenses'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {translate(
                                                        'merge.bulk.group_count_header'
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {translate(
                                                        'merge.bulk.mpass'
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {group.accounts.map(account => (
                                                <React.Fragment
                                                    key={account.guid}
                                                >
                                                    <TableRow>
                                                        <TableCell>
                                                            <Radio
                                                                checked={
                                                                    selection.primaryGuid ===
                                                                    account.guid
                                                                }
                                                                disabled={
                                                                    !isGroupSelectable(
                                                                        group
                                                                    ) ||
                                                                    loading ||
                                                                    mergeLoading
                                                                }
                                                                onChange={() =>
                                                                    setGroupSelection(
                                                                        group,
                                                                        {
                                                                            primaryGuid:
                                                                                account.guid,
                                                                            identifierGuid:
                                                                                selection.identifierGuid ===
                                                                                selection.primaryGuid
                                                                                    ? account.guid
                                                                                    : selection.identifierGuid ||
                                                                                      account.guid,
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Radio
                                                                checked={
                                                                    selection.identifierGuid ===
                                                                    account.guid
                                                                }
                                                                disabled={
                                                                    !isGroupSelectable(
                                                                        group
                                                                    ) ||
                                                                    loading ||
                                                                    mergeLoading
                                                                }
                                                                onChange={() =>
                                                                    setGroupSelection(
                                                                        group,
                                                                        {
                                                                            ...selection,
                                                                            identifierGuid:
                                                                                account.guid,
                                                                        }
                                                                    )
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                spacing={1}
                                                            >
                                                                <Button
                                                                    href={`#/${resource}/${encodeURIComponent(account.guid)}/yhdistaminen`}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    size="small"
                                                                    startIcon={
                                                                        <CallMergeIcon fontSize="small" />
                                                                    }
                                                                    aria-label={translate(
                                                                        'merge.bulk.merge_account',
                                                                        {
                                                                            account:
                                                                                getLogin(
                                                                                    account,
                                                                                    resource
                                                                                ) ||
                                                                                account.guid,
                                                                        }
                                                                    )}
                                                                    disabled={
                                                                        loading ||
                                                                        mergeLoading
                                                                    }
                                                                >
                                                                    {getLogin(
                                                                        account,
                                                                        resource
                                                                    ) || '—'}
                                                                </Button>
                                                                <Button
                                                                    size="small"
                                                                    sx={{
                                                                        whiteSpace:
                                                                            'nowrap',
                                                                        flexShrink: 0,
                                                                    }}
                                                                    disabled={
                                                                        loading ||
                                                                        mergeLoading
                                                                    }
                                                                    onClick={() =>
                                                                        setExpandedAccounts(
                                                                            previous => ({
                                                                                ...previous,
                                                                                [account.guid]:
                                                                                    !previous[
                                                                                        account
                                                                                            .guid
                                                                                    ],
                                                                            })
                                                                        )
                                                                    }
                                                                >
                                                                    {translate(
                                                                        expandedAccounts[
                                                                            account
                                                                                .guid
                                                                        ]
                                                                            ? 'merge.bulk.hide_details'
                                                                            : 'merge.bulk.show_details'
                                                                    )}
                                                                </Button>
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            {formatDate(
                                                                account.last_login
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <AccountCreatedDate
                                                                registered={
                                                                    account.registered
                                                                }
                                                                resource={
                                                                    resource
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Stack
                                                                direction="row"
                                                                alignItems="center"
                                                                flexWrap="wrap"
                                                                useFlexGap
                                                                spacing={0.5}
                                                            >
                                                                {(
                                                                    [
                                                                        'school',
                                                                        'other_school',
                                                                        'own',
                                                                        'unknown',
                                                                    ] as const
                                                                ).map(
                                                                    category => {
                                                                        const activeCount =
                                                                            getActiveLicenseOwnerCounts(
                                                                                account
                                                                            )[
                                                                                category
                                                                            ];
                                                                        if (
                                                                            activeCount ===
                                                                            0
                                                                        )
                                                                            return null;
                                                                        const validCount =
                                                                            getValidLicenseOwnerCounts(
                                                                                account,
                                                                                isLearner
                                                                            )[
                                                                                category
                                                                            ];
                                                                        const categoryLabel =
                                                                            translate(
                                                                                `merge.bulk.license_category_${category}`
                                                                            );
                                                                        return (
                                                                            <StatsTooltip
                                                                                key={
                                                                                    category
                                                                                }
                                                                                describeChild
                                                                                title={
                                                                                    <Box
                                                                                        sx={{
                                                                                            p: 1.5,
                                                                                            maxWidth: 320,
                                                                                        }}
                                                                                    >
                                                                                        <Typography
                                                                                            variant="subtitle2"
                                                                                            sx={{
                                                                                                mb: 0.5,
                                                                                            }}
                                                                                        >
                                                                                            {categoryLabel}
                                                                                        </Typography>
                                                                                        <Typography variant="body2">
                                                                                            {translate(
                                                                                                `merge.bulk.license_category_${category}_help`
                                                                                            )}
                                                                                        </Typography>
                                                                                        <Typography
                                                                                            variant="body2"
                                                                                            sx={{
                                                                                                mt: 0.5,
                                                                                            }}
                                                                                        >
                                                                                            {translate(
                                                                                                'merge.bulk.license_validity_summary',
                                                                                                {
                                                                                                    valid: validCount,
                                                                                                    active: activeCount,
                                                                                                }
                                                                                            )}
                                                                                        </Typography>
                                                                                    </Box>
                                                                                }
                                                                            >
                                                                                <Chip
                                                                                    size="small"
                                                                                    color={getLicenseOwnerColor(
                                                                                        category
                                                                                    )}
                                                                                    label={activeCount}
                                                                                    aria-label={`${categoryLabel}: ${activeCount}`}
                                                                                />
                                                                            </StatsTooltip>
                                                                        );
                                                                    }
                                                                )}
                                                                {getActiveLicenses(account)
                                                                    .length === 0
                                                                    ? '0'
                                                                    : null}
                                                            </Stack>
                                                        </TableCell>
                                                        <TableCell>
                                                            {getVisibleGroupCount(
                                                                account
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            {getServices(
                                                                account
                                                            ).includes(
                                                                'mpass'
                                                            ) ? (
                                                                <CheckIcon
                                                                    color="success"
                                                                    titleAccess="MPASS"
                                                                    fontSize="small"
                                                                />
                                                            ) : (
                                                                '—'
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={8}
                                                            sx={{
                                                                py: 0,
                                                                border: 0,
                                                            }}
                                                        >
                                                            <Collapse
                                                                in={Boolean(
                                                                    expandedAccounts[
                                                                        account
                                                                            .guid
                                                                    ]
                                                                )}
                                                                timeout="auto"
                                                                unmountOnExit
                                                            >
                                                                <Stack
                                                                    direction={{
                                                                        xs: 'column',
                                                                        md: 'row',
                                                                    }}
                                                                    spacing={2}
                                                                    py={1}
                                                                >
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        flexWrap="wrap"
                                                                        useFlexGap
                                                                        spacing={0.5}
                                                                        aria-label={translate(
                                                                            'merge.bulk.license_names'
                                                                        )}
                                                                    >
                                                                        {getActiveLicenses(account).some(license =>
                                                                            Boolean(license.name)
                                                                        ) ? (
                                                                            getDisplayLicenses(
                                                                                getActiveLicenses(account),
                                                                                isLearner
                                                                            ).map(
                                                                                ({
                                                                                    license,
                                                                                    licenses,
                                                                                    versionCount,
                                                                                    validVersionCount,
                                                                                }) => {
                                                                                    const isOverlapping =
                                                                                        licenses.some(
                                                                                            version =>
                                                                                                isOverlappingLicense(
                                                                                                    version,
                                                                                                    overlappingSubscriptionGuids,
                                                                                                    isLearner
                                                                                                )
                                                                                        );
                                                                                    const isExtended =
                                                                                        versionCount >
                                                                                        1;
                                                                                    const extendedLabel =
                                                                                        isExtended
                                                                                            ? translate(
                                                                                                  'merge.bulk.extended_license',
                                                                                                  {
                                                                                                      count: versionCount,
                                                                                                  }
                                                                                              )
                                                                                            : '';
                                                                                    const ownerLabel =
                                                                                        getLicenseOwnerLabel(
                                                                                            license,
                                                                                            account,
                                                                                            translate
                                                                                        );
                                                                                    const ownerCategory =
                                                                                        getLicenseOwnerCategory(
                                                                                            license
                                                                                        );
                                                                                    return (
                                                                                        <StatsTooltip
                                                                                            key={`${license.product_guid || license.name}`}
                                                                                            describeChild
                                                                                            title={
                                                                                                <Box
                                                                                                    sx={{
                                                                                                        p: 1.5,
                                                                                                        maxWidth: 320,
                                                                                                    }}
                                                                                                >
                                                                                                    <Typography
                                                                                                        variant="subtitle2"
                                                                                                        sx={{
                                                                                                            mb: 0.5,
                                                                                                        }}
                                                                                                    >
                                                                                                        {getLicenseTooltipDetails(
                                                                                                            {
                                                                                                                ownerCategory,
                                                                                                                ownerLabel,
                                                                                                                isOverlapping,
                                                                                                                validVersionCount,
                                                                                                                versionCount,
                                                                                                                extendedLabel,
                                                                                                                translate,
                                                                                                            }
                                                                                                        ).title}
                                                                                                    </Typography>
                                                                                                    <Stack
                                                                                                        component="ul"
                                                                                                        spacing={0.25}
                                                                                                        sx={{
                                                                                                            m: 0,
                                                                                                            pl: 2,
                                                                                                        }}
                                                                                                    >
                                                                                                        {getLicenseTooltipDetails(
                                                                                                            {
                                                                                                                ownerCategory,
                                                                                                                ownerLabel,
                                                                                                                isOverlapping,
                                                                                                                validVersionCount,
                                                                                                                versionCount,
                                                                                                                extendedLabel,
                                                                                                                translate,
                                                                                                            }
                                                                                                        ).details.map(
                                                                                                            (
                                                                                                                detail,
                                                                                                                index
                                                                                                            ) => (
                                                                                                                <Typography
                                                                                                                    component="li"
                                                                                                                    variant="body2"
                                                                                                                    sx={{
                                                                                                                        whiteSpace:
                                                                                                                            'pre-line',
                                                                                                                    }}
                                                                                                                    key={`${index}-${detail}`}
                                                                                                                >
                                                                                                                    {detail}
                                                                                                                </Typography>
                                                                                                            )
                                                                                                        )}
                                                                                                    </Stack>
                                                                                                </Box>
                                                                                            }
                                                                                        >
                                                                                            <Box
                                                                                                sx={{
                                                                                                    display: 'inline-flex',
                                                                                                    alignItems: 'center',
                                                                                                    gap: 0.5,
                                                                                                }}
                                                                                            >
                                                                                                {validVersionCount >
                                                                                                0 ? (
                                                                                                    <CheckCircleIcon
                                                                                                        fontSize="small"
                                                                                                        color="success"
                                                                                                    />
                                                                                                ) : (
                                                                                                    <EventBusyIcon
                                                                                                        fontSize="small"
                                                                                                        color="disabled"
                                                                                                    />
                                                                                                )}
                                                                                                <Chip
                                                                                                    label={
                                                                                                        ownerCategory ===
                                                                                                        'school'
                                                                                                            ? license.name
                                                                                                            : `${license.name} · ${ownerLabel}`
                                                                                                    }
                                                                                                    icon={
                                                                                                        isExtended ? (
                                                                                                            <HistoryIcon fontSize="small" />
                                                                                                        ) : undefined
                                                                                                    }
                                                                                                    size="small"
                                                                                                    color={getLicenseOwnerColor(
                                                                                                        ownerCategory
                                                                                                    )}
                                                                                                    variant={
                                                                                                        isOverlapping
                                                                                                            ? 'filled'
                                                                                                            : 'outlined'
                                                                                                    }
                                                                                                    aria-label={`${license.name}, ${ownerLabel}${isOverlapping ? `, ${translate('merge.bulk.overlapping_license')}` : ''}${isExtended ? `, ${extendedLabel}` : ''}`}
                                                                                                />
                                                                                            </Box>
                                                                                        </StatsTooltip>
                                                                                    );
                                                                                }
                                                                            )
                                                                        ) : (
                                                                            <Typography variant="body2">
                                                                                —
                                                                            </Typography>
                                                                        )}
                                                                    </Stack>
                                                                    <Stack
                                                                        direction="row"
                                                                        alignItems="center"
                                                                        flexWrap="wrap"
                                                                        useFlexGap
                                                                        spacing={0.5}
                                                                        aria-label={translate(
                                                                            'merge.bulk.groups'
                                                                        )}
                                                                    >
                                                                        {getVisibleGroups(
                                                                            account
                                                                        ).some(
                                                                            item =>
                                                                                Boolean(
                                                                                    item.name
                                                                                )
                                                                        ) ? (
                                                                            getVisibleGroups(
                                                                                account
                                                                            )
                                                                                .filter(
                                                                                    item =>
                                                                                        Boolean(
                                                                                            item.name
                                                                                        )
                                                                                )
                                                                                .map(
                                                                                    (
                                                                                        item,
                                                                                        index
                                                                                    ) => {
                                                                                        const isOverlapping =
                                                                                            overlappingGroupNames.has(
                                                                                                normalizeName(
                                                                                                    item.name
                                                                                                )
                                                                                            );
                                                                                        return (
                                                                                            <Chip
                                                                                                key={`${normalizeName(item.name)}-${index}`}
                                                                                                label={
                                                                                                    item.name
                                                                                                }
                                                                                                size="small"
                                                                                                color={
                                                                                                    isOverlapping
                                                                                                        ? 'info'
                                                                                                        : 'default'
                                                                                                }
                                                                                                variant={
                                                                                                    isOverlapping
                                                                                                        ? 'filled'
                                                                                                        : 'outlined'
                                                                                                }
                                                                                                sx={{
                                                                                                    borderColor:
                                                                                                        theme =>
                                                                                                            alpha(
                                                                                                                theme.palette
                                                                                                                    .info
                                                                                                                    .main,
                                                                                                                isOverlapping
                                                                                                                    ? 1
                                                                                                                    : 0.35
                                                                                                            ),
                                                                                                }}
                                                                                                aria-label={`${item.name}${isOverlapping ? `, ${translate('merge.bulk.overlapping_group')}` : ''}`}
                                                                                            />
                                                                                        );
                                                                                    }
                                                                                )
                                                                        ) : (
                                                                            <Typography variant="body2">
                                                                                —
                                                                            </Typography>
                                                                        )}
                                                                    </Stack>
                                                                </Stack>
                                                            </Collapse>
                                                        </TableCell>
                                                    </TableRow>
                                                </React.Fragment>
                                            ))}
                                        </TableBody>
                                    </Table>

                                    {mode === 'merge' && result && (
                                        <Alert
                                            severity={
                                                result.status === 'success'
                                                    ? result.historyFetchFailed
                                                        ? 'warning'
                                                        : 'success'
                                                    : result.status ===
                                                        'cancelled'
                                                      ? 'info'
                                                      : 'error'
                                            }
                                            sx={{ mt: 1 }}
                                        >
                                            {translate(
                                                `merge.bulk.result_${result.status}`
                                            )}
                                            {result.message
                                                ? `: ${result.message}`
                                                : ''}
                                            {(result.status === 'error' ||
                                                result.status ===
                                                    'partial') && (
                                                <Button
                                                    size="small"
                                                    onClick={() =>
                                                        loadGroups(
                                                            group.key,
                                                            true
                                                        )
                                                    }
                                                >
                                                    {translate(
                                                        'merge.bulk.retry'
                                                    )}
                                                </Button>
                                            )}
                                            {result.merges?.map(merge => (
                                                <Box
                                                    key={merge.guid}
                                                    sx={{ mt: 0.5 }}
                                                >
                                                    {canCancelMerge &&
                                                        merge.cancellable &&
                                                        !merge.materials_merged && (
                                                            <Button
                                                                size="small"
                                                                onClick={() =>
                                                                    setCancelTarget(
                                                                        {
                                                                            groupKey:
                                                                                group.key,
                                                                            merge,
                                                                        }
                                                                    )
                                                                }
                                                            >
                                                                {translate(
                                                                    'merge.profilepage.cancel_button'
                                                                )}
                                                            </Button>
                                                        )}
                                                    {!merge.cancellable && (
                                                        <Typography
                                                            variant="caption"
                                                            display="block"
                                                        >
                                                            {merge.materials_merged
                                                                ? translate(
                                                                      'merge.profilepage.cancel_materials_merged'
                                                                  )
                                                                : translate(
                                                                      'merge.profilepage.cancel_deadline_passed'
                                                                  )}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            ))}
                                        </Alert>
                                    )}
                                </Paper>
                            );
                        })}
                    </Stack>

                    <ListPaginationContext.Provider
                        value={{
                            resource: resource || 'user',
                            page: page + 1,
                            perPage: pageSize,
                            total: filteredGroups.length,
                            hasNextPage:
                                (page + 1) * pageSize < filteredGroups.length,
                            hasPreviousPage: page > 0,
                            isLoading: loading,
                            isPending: loading,
                            setPage: value => setPage(value - 1),
                            setPerPage: value => {
                                setPageSize(value);
                                setPage(0);
                            },
                        }}
                    >
                        <Pagination
                            rowsPerPageOptions={[10, 25, 50, 100, 200]}
                        />
                    </ListPaginationContext.Provider>

                    {mode === 'merge' && (
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            justifyContent="flex-end"
                        >
                            <Button
                                variant="outlined"
                                disabled={
                                    selectedGroups.length === 0 ||
                                    previewLoading ||
                                    mergeLoading ||
                                    selectedGroups.some(group => {
                                        const selection = selections[group.key];
                                        return (
                                            !selection?.primaryGuid ||
                                            !selection?.identifierGuid
                                        );
                                    })
                                }
                                onClick={handlePreview}
                            >
                                {previewLoading && (
                                    <CircularProgress size={16} sx={{ mr: 1 }} />
                                )}
                                {translate('merge.bulk.preview')}
                            </Button>
                        </Stack>
                    )}
                </>
            )}

            <BulkMergeDialog
                previewOpen={Boolean(previews)}
                canConfirm={
                    canAccess &&
                    Boolean(previews?.length) &&
                    !previews?.some(pair => pair.error)
                }
                groupCount={selectedGroups.length}
                progress={mergeProgress}
                stopRequested={stopRequested}
                onClose={handleCloseMergeDialog}
                onConfirm={handleMerge}
                onStop={handleStopMerge}
            >
                {previews?.map((pair, index) => {
                    const group = groups.find(
                        item => item.key === pair.groupKey
                    );
                    const primary = group?.accounts.find(
                        account => account.guid === pair.toGuid
                    );
                    const source = group?.accounts.find(
                        account => account.guid === pair.fromGuid
                    );
                    return (
                        <Alert
                            key={`${pair.groupKey}-${pair.fromGuid}-${index}`}
                            severity={
                                pair.error
                                    ? 'error'
                                    : pair.warnings.length
                                      ? 'warning'
                                      : 'info'
                            }
                            sx={{ mt: 1 }}
                        >
                            {pair.groupName}: {getLogin(source!, resource)} →{' '}
                            {getLogin(primary!, resource)}
                            {pair.warnings.length > 0 && (
                                <Typography variant="body2">
                                    {pair.warnings.join('; ')}
                                </Typography>
                            )}
                            {pair.error && (
                                <Typography variant="body2">
                                    {pair.error}
                                </Typography>
                            )}
                        </Alert>
                    );
                })}
                <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {translate('merge.bulk.preview_notice')}
                </Typography>
            </BulkMergeDialog>

            <Dialog
                open={Boolean(cancelTarget)}
                onClose={() => setCancelTarget(null)}
            >
                <DialogTitle>
                    {translate('merge.profilepage.cancel_confirm_title')}
                </DialogTitle>
                <DialogContent>
                    {translate('merge.profilepage.cancel_confirm_message')}
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={cancelLoading}
                        onClick={() => setCancelTarget(null)}
                    >
                        {translate('merge.cancel')}
                    </Button>
                    <Button
                        color="error"
                        disabled={cancelLoading}
                        onClick={handleCancelMerge}
                    >
                        {cancelLoading && (
                            <CircularProgress size={16} sx={{ mr: 1 }} />
                        )}
                        {translate('merge.profilepage.cancel_button')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BulkMergePage;
